// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — three ways to run the common (principle #14). The rule is that a mode must change how
// the game PLAYS, not just a number, so the third one changes a RULE rather than a dimension:
//
//   Heath     9x9, ten hedges each. The long game. Routes are long enough that a hedge planted
//             early is still shaping the race twenty plies later, and there is room to build a
//             real maze rather than a single detour.
//   Fold      7x7, six hedges each. Every hedge is a bigger fraction of a shorter route, so the
//             game is sharp and tactical and finishes in about half the plies.
//   Coppice   9x9, but ONE SHARED STACK of twelve. You are no longer spending your own supply,
//             you are spending the supply — so a mediocre block can still be the right move purely
//             to take it out of a rival's hands, and running the pile dry turns the rest of the
//             race into a pure footrace with the maze already fixed. Same board as Heath and it
//             plays nothing like it.
//
// The host's pick travels FROZEN inside the round start (rematch roundOpts) and a guest renders
// state().hostOpts — a setting each peer read from its own UI is a setting two peers can disagree
// about, and here it would mean two different board sizes on the same seed. An id off the wire is
// always modeOf()-validated with Object.hasOwn, so a wire key can never resolve a prototype member.

export type ModeId = 'heath' | 'fold' | 'coppice';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Cells per side. Always odd, so both runners start dead centre of their edge. */
  readonly size: number;
  /**
   * Hedges each runner starts with. In a shared-stack mode this is 0 and `pool` carries them
   * instead — the two are deliberately exclusive so there is exactly one place supply lives.
   */
  readonly hedges: number;
  /** A stack every runner draws from, or 0 when each has their own supply. */
  readonly pool: number;
  /** Plies before the race is called a draw, so a mutual stall can never hang a room. */
  readonly plyCap: number;
}

export const MODES: Record<ModeId, Mode> = {
  heath: {
    id: 'heath',
    name: 'Heath',
    blurb: 'Nine by nine, ten hedges each. Long routes, room for a real maze.',
    size: 9,
    hedges: 10,
    pool: 0,
    plyCap: 400,
  },
  fold: {
    id: 'fold',
    name: 'Fold',
    blurb: 'Seven by seven, six hedges each. Short routes — every hedge bites harder.',
    size: 7,
    hedges: 6,
    pool: 0,
    plyCap: 300,
  },
  coppice: {
    id: 'coppice',
    name: 'Coppice',
    blurb: 'Nine by nine and one shared stack of twelve. Planting is also denying.',
    size: 9,
    hedges: 0,
    pool: 12,
    plyCap: 400,
  },
};

export const MODE_IDS: readonly ModeId[] = ['heath', 'fold', 'coppice'];

/** Validate an id that may have come off the wire or out of storage. Never returns undefined. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id as ModeId];
  return MODES.heath;
}

/** Does this mode draw hedges from one shared stack? */
export const isShared = (m: Mode): boolean => m.pool > 0;
